import Modal from "components/DesignSystem/Modal/Modal";
import { Stepper } from "components/DesignSystem/Stepper/Stepper";
import { SwitchCase } from "components/DesignSystem/SwitchCase/SwitchCase";
import { TakeConfirmationModal } from "components/TakeConfirmationModal/TakeConfirmationModal";
import { ArrowRight } from "components/icons/ArrowRight";
import {
  QBO_STEPS,
  QBO_STEPS_BREADCRUMBS,
  QBO_STEPS_TYPE,
  QBO_STEP_ORDER,
} from "constants/qboImportSteps";
import { ModalClose, ModalControl } from "hooks/useModal";
import { useWindowSize } from "hooks/useWindowSize";
import { createContext, useContext, useState } from "react";
import { AddDetailsFormValues, AddDetailsStep } from "./AddDetailsStep";
import {
  BankMapFormValues,
  MapDataConnectionsStepContent,
} from "components/ChartOfAccounts/ImportCOA/MapDataConnectionsStep";
import {
  FormValues as CategoryMapFormValues,
  MapCategoriesStepContent,
} from "components/ChartOfAccounts/ImportCOA/MapCategoriesStep";
import { HowItWorksModal } from "./HowItWorksModal";
import { ReviewStep } from "./ReviewStep";
import { DefaultCategoriesStep } from "./DefaultCategoriesStep";
import { useGetFileUploadQBOfileQuery } from "store/apis/qbo";
import { GetQBOFileResponse } from "types/Models/books";

type ImportQBOProps = {
  entityId: string;
} & ModalControl;

type ResumeState = {
  step: QBO_STEPS_TYPE;
  importProcessId: string | null;
};

interface ImportQBOContextType {
  currentStep: QBO_STEPS_TYPE;
  setCurrentStep: (step: QBO_STEPS_TYPE) => void;
  qboCSV: File | null;
  setQboCSV: (file: File | null) => void;
  importProcessId: string | null;
  setImportProcessId: (id: string | null) => void;
  entityId: string;
  onNext: () => void;
  onStepChange: (step: QBO_STEPS_TYPE) => void;
  addDetailsFormValues: AddDetailsFormValues | null;
  setAddDetailsFormValues: (values: AddDetailsFormValues) => void;
  bankMapFormValues: BankMapFormValues | null;
  setBankMapFormValues: (values: BankMapFormValues) => void;
  categoryMapFormValues: CategoryMapFormValues | null;
  setCategoryMapFormValues: (values: CategoryMapFormValues) => void;
  isOpen: boolean;
  close: ModalClose;
  showConfirmation: boolean;
  dismissConfirmation: () => void;
}

const ImportQBOContext = createContext<ImportQBOContextType | undefined>(
  undefined
);

export const useImportQBO = () => {
  const context = useContext(ImportQBOContext);
  if (context === undefined) {
    throw new Error("useImportQBO must be used within an ImportQBOProvider");
  }
  return context;
};

interface ImportQBOProviderProps {
  children: React.ReactNode;
  entityId: string;
  isOpen: boolean;
  close: ModalClose;
  showConfirmation: boolean;
  dismissConfirmation: () => void;
  resumeState: ResumeState;
}

const getResumeState = (data?: GetQBOFileResponse): ResumeState => {
  const importProcessId = data?.coa_import_state_id ?? null;

  if (!importProcessId) {
    return { step: QBO_STEPS.ADD_DETAILS, importProcessId: null };
  }

  if (data?.default_category_mapping) {
    return { step: QBO_STEPS.DEFAULT_CATEGORIES, importProcessId };
  }

  return { step: QBO_STEPS.MAP_DATA_CONNECTIONS, importProcessId };
};

const ImportQBOProvider = ({
  children,
  entityId,
  isOpen,
  close,
  showConfirmation,
  dismissConfirmation,
  resumeState,
}: ImportQBOProviderProps) => {
  const [qboCSV, setQboCSV] = useState<File | null>(null);
  const [currentStep, setCurrentStep] = useState<QBO_STEPS_TYPE>(
    resumeState.step
  );
  const [importProcessId, setImportProcessId] = useState<string | null>(
    resumeState.importProcessId
  );
  const [addDetailsFormValues, setAddDetailsFormValues] =
    useState<AddDetailsFormValues | null>(null);
  const [bankMapFormValues, setBankMapFormValues] =
    useState<BankMapFormValues | null>(null);
  const [categoryMapFormValues, setCategoryMapFormValues] =
    useState<CategoryMapFormValues | null>(null);

  const onNext = () => {
    if (currentStep === QBO_STEPS.REVIEW) {
      close();
      setCurrentStep(QBO_STEPS.ADD_DETAILS);
      setQboCSV(null);
      setImportProcessId(null);
      return;
    }

    setCurrentStep(QBO_STEP_ORDER[QBO_STEP_ORDER.indexOf(currentStep) + 1]);
  };

  const onStepChange = (step: QBO_STEPS_TYPE) => {
    setCurrentStep(step);
  };

  const contextValue: ImportQBOContextType = {
    currentStep,
    setCurrentStep,
    qboCSV,
    setQboCSV,
    importProcessId,
    setImportProcessId,
    entityId,
    onNext,
    onStepChange,
    addDetailsFormValues,
    setAddDetailsFormValues,
    bankMapFormValues,
    setBankMapFormValues,
    categoryMapFormValues,
    setCategoryMapFormValues,
    isOpen,
    close,
    showConfirmation,
    dismissConfirmation,
  };

  return (
    <ImportQBOContext.Provider value={contextValue}>
      {children}
    </ImportQBOContext.Provider>
  );
};

const ImportStepper = () => {
  const { currentStep, onStepChange } = useImportQBO();
  const { isMobile } = useWindowSize();
  const currentIndex = QBO_STEP_ORDER.indexOf(currentStep);

  // Five steps can't fit a phone header, so phones show just the current step.
  if (isMobile) {
    const currentStepName = QBO_STEPS_BREADCRUMBS.find(
      ({ step }) => step === currentStep
    )?.name;

    return (
      <span className="t-text-body-sm t-text-purple">
        Step {currentIndex + 1} of {QBO_STEPS_BREADCRUMBS.length} ·{" "}
        {currentStepName}
      </span>
    );
  }

  return (
    <Stepper size="xs" direction="horizontal" className="!t-gap-0">
      {QBO_STEPS_BREADCRUMBS.map(({ name, step, no }) => (
        <Stepper.Step
          key={step}
          step={no}
          isActive={currentIndex >= QBO_STEP_ORDER.indexOf(step)}
          onClick={() => onStepChange(step)}
          clickable={currentIndex > QBO_STEP_ORDER.indexOf(step)}
        >
          <div className="t-flex t-items-center">
            {name}
            {step !== QBO_STEPS.REVIEW && <ArrowRight color="currentColor" />}
          </div>
        </Stepper.Step>
      ))}
    </Stepper>
  );
};

const QBOMapDataConnectionsStep = ({
  onNext,
  importProcessId,
  entityId,
}: {
  onNext: () => void;
  importProcessId: string;
  entityId: string;
}) => {
  const { bankMapFormValues, setBankMapFormValues, isOpen, close } =
    useImportQBO();

  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={() => close({ takeConfirmation: true })}
      modal={false}
    >
      <Modal.Content size="xl" useCustomOverlay>
        <MapDataConnectionsStepContent
          onNext={onNext}
          importProcessId={importProcessId}
          entityId={entityId}
          header={<ImportQBOHeader />}
          initialValues={bankMapFormValues || undefined}
          onFormValuesChange={setBankMapFormValues}
          additionalFooterButtons={<HowItWorksModal />}
        />
      </Modal.Content>
    </Modal.Root>
  );
};

const QBOMapCategoriesStep = ({
  onNext,
  importProcessId,
  entityId,
}: {
  onNext: () => void;
  importProcessId: string;
  entityId: string;
}) => {
  const { categoryMapFormValues, setCategoryMapFormValues, isOpen, close } =
    useImportQBO();

  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={() => close({ takeConfirmation: true })}
      modal={false}
    >
      <Modal.Content size="xl" useCustomOverlay>
        <MapCategoriesStepContent
          onNext={onNext}
          importProcessId={importProcessId}
          entityId={entityId}
          header={<ImportQBOHeader />}
          initialValues={categoryMapFormValues || undefined}
          onFormValuesChange={setCategoryMapFormValues}
          additionalFooterButtons={<HowItWorksModal />}
        />
      </Modal.Content>
    </Modal.Root>
  );
};

const ImportQBOSteps = () => {
  const { currentStep, onNext, importProcessId, entityId, setImportProcessId } =
    useImportQBO();

  return (
    <SwitchCase
      value={currentStep}
      cases={{
        [QBO_STEPS.ADD_DETAILS]: (
          <AddDetailsStep
            onNext={onNext}
            entityId={entityId}
            setImportProcessId={setImportProcessId}
          />
        ),
        [QBO_STEPS.MAP_DATA_CONNECTIONS]: importProcessId && (
          <QBOMapDataConnectionsStep
            importProcessId={importProcessId}
            entityId={entityId}
            onNext={onNext}
          />
        ),
        [QBO_STEPS.MAP_CATEGORIES]: importProcessId && (
          <QBOMapCategoriesStep
            importProcessId={importProcessId}
            entityId={entityId}
            onNext={onNext}
          />
        ),
        [QBO_STEPS.DEFAULT_CATEGORIES]: importProcessId && (
          <DefaultCategoriesStep
            importProcessId={importProcessId}
            entityId={entityId}
            onNext={onNext}
          />
        ),
        [QBO_STEPS.REVIEW]: importProcessId && (
          <ReviewStep
            importProcessId={importProcessId}
            entityId={entityId}
            onNext={onNext}
          />
        ),
      }}
    />
  );
};

export const ImportQBOHeader = () => {
  return (
    <Modal.Header className="sm:t-pl-4">
      <div className="t-flex t-flex-col t-gap-2">
        <Modal.Title>Import data from Quickbooks</Modal.Title>
        <ImportStepper />
      </div>
      <Modal.Close />
    </Modal.Header>
  );
};

export const ImportQBO = ({
  isOpen,
  close,
  showConfirmation,
  dismissConfirmation,
  entityId,
}: ImportQBOProps) => {
  const { data, isLoading } = useGetFileUploadQBOfileQuery(
    { entityId },
    { skip: !entityId }
  );

  if (isLoading) {
    return null;
  }

  return (
    <>
      <ImportQBOProvider
        entityId={entityId}
        isOpen={isOpen}
        close={close}
        showConfirmation={showConfirmation}
        dismissConfirmation={dismissConfirmation}
        resumeState={getResumeState(data)}
      >
        <ImportQBOSteps />
      </ImportQBOProvider>
      <TakeConfirmationModal
        isOpen={showConfirmation}
        dismiss={dismissConfirmation}
        close={close}
      />
    </>
  );
};
